import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const extensionAreas = [
  //"Test",
  "Ennerdale",
  "Lenasia",
  "Toekomsrus",
  "Lehae",
  "Klipspruit West",
  "Kliptown",
  "Ext. 9",
  "Ext. 8",
  "Ext. 7",
  "Ext. 6",
  "Old Eldos",
  "Mahala Park",
  "Ext. 1",
  "Bushkoppies",
  "Ext. 3",
  "Ext. 2",
  "Ext. 4",
  "Freedom Park",
  "Devland",
  "Naturena",
  "Mondeor and South",
  "East Rand"
];

const formSchema = z.object({
  name: z.string().min(1, "First name is required"),
  surname: z.string().min(1, "Surname is required"),
  home_address: z.string().min(5, "Home address is required"),
  gender: z.string().min(1, "Gender is required"),
  contact_number: z.string().min(10, "Valid contact number is required"),
  email: z.string().email("Valid email is required").optional().or(z.literal("")),
  extension_area: z.string().min(1, "Extension/Area is required"),
});

export const FellowshipForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedGender, setSelectedGender] = useState("");
  const [selectedExtension, setSelectedExtension] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/submit-fellowship`, data);
      toast.success("Welcome new Vision Bearer! Your details have been submitted and a Zone Minister will get in touch with you soon.\nGod Bless!");
      reset();
      setSelectedGender("");
      setSelectedExtension("");
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to submit. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 min-h-screen overflow-y-auto">
      {/* Left Panel - Hero Section */}
      <div className="lg:col-span-5 relative hidden lg:flex flex-col justify-between p-12 text-white overflow-y-auto max-h-screen scrollbar-thin scrollbar-thumb-stone-400">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url("/rhof.jpg")',
          }}
        />
        <div className="absolute inset-0 bg-stone-900/30 backdrop-blur-[2px]" />
        
        <div className="relative z-10">
          <h1 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Right Hand of Fellowship
          </h1>
          <p className="text-lg text-white/90" style={{ fontFamily: 'Inter, sans-serif' }}>
            Join our community of faith and fellowship
          </p>
        </div>

        <div className="relative z-10 backdrop-blur-md bg-white/10 border border-white/20 rounded-2xl p-8 shadow-xl">
          <p className="text-xl italic leading-relaxed" style={{ fontFamily: 'Playfair Display, serif' }}>
            "Behold, how good and how pleasant it is for brethren to dwell together in unity!"
          </p>
          <p className="mt-3 text-white/80" style={{ fontFamily: 'Inter, sans-serif' }}>— Psalm 133:1</p>
        </div>
      </div>

      {/* Mobile Hero/Header */}
      <div className="flex lg:hidden w-full mb-6">
        <div className="flex items-center w-full bg-[#063866] text-white p-4 shadow-md">
          <img
            src="/rhof.jpg"
            alt="Right Hand of Fellowship"
            className="w-18 h-18 rounded-xl object-cover mr-4 border border-white/30"
          />
          <div>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
              Right Hand of Fellowship
            </h1>
            <p className="text-xs text-white/80" style={{ fontFamily: 'Inter, sans-serif' }}>
              Join our community of faith and fellowship
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Form Section */}
      <div className="lg:col-span-7 bg-stone-50 flex items-center justify-center p-4 sm:p-6 md:p-12 overflow-y-auto">
        <div className="w-full max-w-2xl space-y-6 sm:space-y-8 bg-white p-6 sm:p-8 md:p-12 rounded-2xl shadow-sm border border-stone-100 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="space-y-2 sm:space-y-3">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Registration Form
            </h2>
            <p className="text-sm sm:text-base text-stone-600" style={{ fontFamily: 'Inter, sans-serif' }}>
              Please fill in all the required fields below
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium text-stone-900">
                First Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                data-testid="name-input"
                placeholder="e.g. John"
                {...register("name")}
                className="h-10 sm:h-12 rounded-xl border-stone-200 focus:ring-2 focus:ring-[#063866]/20 focus:ring-offset-1 transition-all"
                disabled={isSubmitting}
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name.message}</p>
              )}
            </div>

            {/* Surname */}
            <div className="space-y-2">
              <Label htmlFor="surname" className="text-sm font-medium text-stone-900">
                Surname <span className="text-red-500">*</span>
              </Label>
              <Input
                id="surname"
                data-testid="surname-input"
                placeholder="e.g. Doe"
                {...register("surname")}
                className="h-10 sm:h-12 rounded-xl border-stone-200 focus:ring-2 focus:ring-[#063866]/20 focus:ring-offset-1 transition-all"
                disabled={isSubmitting}
              />
              {errors.surname && (
                <p className="text-sm text-red-500">{errors.surname.message}</p>
              )}
            </div>

            {/* Gender */}
            <div className="space-y-2">
              <Label htmlFor="gender" className="text-sm font-medium text-stone-900">
                Gender <span className="text-red-500">*</span>
              </Label>
              <Select
                value={selectedGender}
                onValueChange={(value) => {
                  setSelectedGender(value);
                  setValue("gender", value);
                }}
                disabled={isSubmitting}
              >
                <SelectTrigger
                  data-testid="gender-select"
                  className="h-12 rounded-xl border-stone-200 focus:ring-2 focus:ring-[#063866]/20 focus:ring-offset-1 transition-all"
                >
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                </SelectContent>
              </Select>
              {errors.gender && (
                <p className="text-sm text-red-500">{errors.gender.message}</p>
              )}
            </div>

            {/* Contact Number */}
            <div className="space-y-2">
              <Label htmlFor="contact_number" className="text-sm font-medium text-stone-900">
                Contact Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="contact_number"
                data-testid="contact-number-input"
                type="tel"
                placeholder="+27 123 456 7890"
                {...register("contact_number")}
                className="h-12 rounded-xl border-stone-200 focus:ring-2 focus:ring-[#063866]/20 focus:ring-offset-1 transition-all"
                disabled={isSubmitting}
              />
              {errors.contact_number && (
                <p className="text-sm text-red-500">{errors.contact_number.message}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-stone-900">
                Email Address
              </Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                placeholder="john@example.com"
                {...register("email")}
                className="h-12 rounded-xl border-stone-200 focus:ring-2 focus:ring-[#063866]/20 focus:ring-offset-1 transition-all"
                disabled={isSubmitting}
              />
              {errors.email && (
                <p className="text-sm text-red-500">{errors.email.message}</p>
              )}
            </div>

            {/* Home Address */}
            <div className="space-y-2">
              <Label htmlFor="home_address" className="text-sm font-medium text-stone-900">
                Home Address <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="home_address"
                data-testid="home-address-textarea"
                placeholder="Your full residential address"
                {...register("home_address")}
                className="min-h-24 rounded-xl border-stone-200 focus:ring-2 focus:ring-[#063866]/20 focus:ring-offset-1 transition-all resize-none"
                disabled={isSubmitting}
              />
              {errors.home_address && (
                <p className="text-sm text-red-500">{errors.home_address.message}</p>
              )}
            </div>

            {/* Extension/Area */}
            <div className="space-y-2">
              <Label htmlFor="extension_area" className="text-sm font-medium text-stone-900">
                Extension / Area <span className="text-red-500">*</span>
              </Label>
              <Select
                value={selectedExtension}
                onValueChange={(value) => {
                  setSelectedExtension(value);
                  setValue("extension_area", value);
                }}
                disabled={isSubmitting}
              >
                <SelectTrigger
                  data-testid="extension-area-select"
                  className="h-12 rounded-xl border-stone-200 focus:ring-2 focus:ring-[#063866]/20 focus:ring-offset-1 transition-all"
                >
                  <SelectValue placeholder="Select your area" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {extensionAreas.map((area) => (
                    <SelectItem key={area} value={area}>
                      {area}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.extension_area && (
                <p className="text-sm text-red-500">{errors.extension_area.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              data-testid="submit-button"
              disabled={isSubmitting}
              className="w-full bg-[#063866] hover:bg-[#0B1B38] text-white h-12 text-lg font-medium rounded-xl transition-all duration-300 shadow-md hover:shadow-lg"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-5 w-5" />
                  Submit Registration
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FellowshipForm;
